import { useState } from "react";
import { Loader2, UsersRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { useAdminUsers, useCreateAdminTeam } from "@/hooks/use-admin-users";
import { useTranslations } from "@/hooks/use-translations";

/** A new team with the user the Super Admin picks as its Owner. */
export function CreateTeamForm() {
	const [name, setName] = useState("");
	const [ownerId, setOwnerId] = useState("");
	const users = useAdminUsers();
	const create = useCreateAdminTeam();
	const t = useTranslations("manager.createTeam");
	const owners = (users.data ?? []).filter((u) => u.isActive);
	const isValid = name.trim().length > 0 && ownerId !== "";

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (!isValid || create.isPending) return;
		create.mutate(
			{ name: name.trim(), ownerId },
			{
				onSuccess: () => {
					setName("");
					setOwnerId("");
				},
			},
		);
	};

	// A section of the page's card (Manager page), styled like the Connectors card.
	return (
		<div>
			<h3 className="text-sm font-medium text-text-primary">{t("title")}</h3>
			<p className="text-sm text-text-tertiary">{t("description")}</p>
			<form onSubmit={handleSubmit} className="mt-4 space-y-5">
				<div className="grid gap-4 md:grid-cols-2">
					<Input
						id="new-team-name"
						label={t("name")}
						value={name}
						onChange={(e) => setName(e.target.value)}
						placeholder={t("namePlaceholder")}
						disabled={create.isPending}
					/>
					<Select
						id="new-team-owner"
						label={t("owner")}
						value={ownerId}
						onChange={(e) => setOwnerId(e.target.value)}
						disabled={create.isPending || users.isLoading}
					>
						<option value="">{t("pickOwner")}</option>
						{owners.map((u) => (
							<option key={u.id} value={u.id}>
								{u.name} ({u.email})
							</option>
						))}
					</Select>
				</div>
				<Button type="submit" variant="secondary" disabled={!isValid || create.isPending}>
					{create.isPending ? <Loader2 size={16} className="animate-spin" /> : <UsersRound size={16} />}
					{t(create.isPending ? "submitting" : "submit")}
				</Button>
			</form>
		</div>
	);
}
